import React, {useEffect} from 'react';
import {Routes, Route, Navigate, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from './store/store';
import './i18n/i18n'; // Initialize i18n
import analytics from './utils/analytics'; // Google Analytics

// Layouts
import PublicLayout from './layouts/PublicLayout';
import AdminLayout from './layouts/AdminLayout';
import PrivateRoute from './components/common/PrivateRoute';

// Public Pages
import HomePage from './pages/public/HomePage';
import Shop from './pages/public/Shop';
import ProductDetailPage from './pages/public/ProductDetailPage';
import CartPage from './pages/public/CartPage';
import CheckoutPage from './pages/public/CheckoutPage';
import GuestCheckoutPage from './pages/public/GuestCheckoutPage';
import OrderTracking from './pages/public/OrderTracking';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import SearchResults from './pages/public/SearchResults';
import ProfilePage from './pages/user/ProfilePage';
import OrdersPage from './pages/user/OrdersPage';
import OrderDetailPage from './pages/user/OrderDetailPage';

// Legal Pages
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import CookiePolicy from './pages/legal/CookiePolicy';
import ReturnPolicy from './pages/legal/ReturnPolicy';
import DeliveryInfo from './pages/legal/DeliveryInfo';
import Contact from './pages/legal/Contact';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import ReviewModerationPage from './pages/admin/ReviewModerationPage';
import AdminProducts from './pages/admin/Products';
import AdminProductEdit from './pages/admin/ProductEdit';
import AdminOrders from './pages/admin/Orders';
import AdminOrderDetail from './pages/admin/OrderDetail';
import AdminUsers from './pages/admin/Users';
import AdminUserDetail from './pages/admin/UserDetail';
import AdminCategories from './pages/admin/Categories';
import AdminSettings from './pages/admin/Settings';

import {loadUser} from './store/slices/authSlice';
import {fetchCart} from './store/slices/cartSlice';
import {initializeCsrf} from './services/csrf';

const App: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const {isAuthenticated, user} = useSelector((state: RootState) => state.auth);

    // Initialize CSRF and Google Analytics
    useEffect(() => {
        const initializeApp = async () => {
            await initializeCsrf();
            analytics.init();
        };

        initializeApp();
    }, [dispatch]);

    // Handle cart loading based on authentication state
    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchCart());
        }
    }, [dispatch, isAuthenticated]);
    // Scroll to top and track page views on route change
    useEffect(() => {
        window.scrollTo(0, 0);
        analytics.pageView(location.pathname + location.search);
    }, [location]);

    // Set user ID when authenticated
    useEffect(() => {
        if (user?.id) {
            analytics.setUserId(user.id.toString());
        }
    }, [user]);

    useEffect(() => {
        dispatch(loadUser());
    }, [dispatch]);

    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicLayout/>}>
                <Route index element={<HomePage/>}/>
                <Route path="shop" element={<Shop/>}/>
                <Route path="products" element={<Shop/>}/>
                <Route path="products/:id" element={<ProductDetailPage/>}/>
                <Route path="cart" element={<CartPage/>}/>
                <Route path="checkout/guest" element={<GuestCheckoutPage/>}/>
                <Route path="order-tracking" element={<OrderTracking/>}/>
                <Route path="search" element={<SearchResults/>}/>
                <Route path="login" element={<Login/>}/>
                <Route path="register" element={<Register/>}/>
                <Route path="forgot-password" element={<ForgotPassword/>}/>
                <Route path="reset-password" element={<ResetPassword/>}/>

                {/* Legal Pages */}
                <Route path="legal/terms" element={<TermsOfService/>}/>
                <Route path="legal/privacy" element={<PrivacyPolicy/>}/>
                <Route path="legal/cookies" element={<CookiePolicy/>}/>
                <Route path="legal/returns" element={<ReturnPolicy/>}/>
                <Route path="legal/delivery" element={<DeliveryInfo/>}/>
                <Route path="legal/contact" element={<Contact/>}/>

                {/* Legacy routes for backward compatibility */}
                <Route path="terms" element={<TermsOfService/>}/>
                <Route path="privacy" element={<PrivacyPolicy/>}/>
                <Route path="returns" element={<ReturnPolicy/>}/>
                <Route path="shipping" element={<DeliveryInfo/>}/>
                <Route path="contact" element={<Contact/>}/>

                {/* Protected User Routes */}
                <Route element={<PrivateRoute/>}>
                    <Route path="checkout" element={<CheckoutPage/>}/>
                    <Route path="profile" element={<ProfilePage/>}/>
                    <Route path="orders" element={<OrdersPage/>}/>
                    <Route path="orders/:id" element={<OrderDetailPage/>}/>
                </Route>
            </Route>

            {/* Admin Routes */}
            <Route
                path="/admin"
                element={
                    <PrivateRoute requireAdmin>
                        <AdminLayout/>
                    </PrivateRoute>
                }
            >
                <Route index element={<AdminDashboard/>}/>
                <Route path="products" element={<AdminProducts/>}/>
                <Route path="products/new" element={<AdminProductEdit/>}/>
                <Route path="products/:id/edit" element={<AdminProductEdit/>}/>
                <Route path="orders" element={<AdminOrders/>}/>
                <Route path="orders/:id" element={<AdminOrderDetail/>}/>
                <Route path="reviews" element={<ReviewModerationPage/>}/>
                <Route path="users" element={<AdminUsers/>}/>
                <Route path="users/:id" element={<AdminUserDetail/>}/>
                <Route path="categories" element={<AdminCategories/>}/>
                <Route path="settings" element={<AdminSettings/>}/>
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace/>}/>
        </Routes>
    );
};

export default App;